// Slice 12 — CGC member sign-in entry (DESIGN §5.5 / §9.3). Submits an
// email to `/auth/cgc/request`; the response is intentionally identical for
// known/unknown emails (enumeration defence — DESIGN §4.6). The follow-up
// TOTP flow lives at `/cgc/totp` after the adviser clicks the email link.
//
// Mirror of [`AdminSignIn`](./AdminSignIn.tsx) — same posture, different
// audience (external clinical / legal advisers approving system prompts).

import { useState, type FormEvent } from 'react';
import { Link, redirect } from 'react-router';
import { Logo } from '@/components/brand/Logo.tsx';
import { api } from '@/lib/api.ts';
import { fetchMe, landingPathFor } from '@/lib/auth.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Field, TextInput } from '@/components/ui/Field.tsx';

export async function cgcSignInLoader() {
  const session = await fetchMe();
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  if (session) throw redirect(landingPathFor(session.role));
  return null;
}

export function CgcSignIn() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await api.post('/auth/cgc/request', { email });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Try again in a moment.');
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-6 py-12">
      <Logo className="text-2xl" />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">CGC sign-in</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        For Clinical Governance Council members only — reviewing and approving Maeve system prompts.
        Tenant users and besideU operators have separate sign-in pages;{' '}
        <Link to="/login" className="underline underline-offset-2 hover:no-underline">
          see all sign-in options
        </Link>
        .
      </p>

      {submitted ? (
        <div className="mt-8 rounded-lg border border-border bg-muted/30 p-6 text-sm">
          <p className="font-medium">If your address is registered, a link is on its way.</p>
          <p className="mt-2 text-muted-foreground">
            The link expires in 15 minutes. After clicking, you&rsquo;ll be asked for a code from
            your authenticator app.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-8 grid gap-5">
          <Field label="Email">
            {(id, descProps) => (
              <TextInput
                id={id}
                {...descProps}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" loading={pending}>
            Send sign-in link
          </Button>
          {error ? (
            <p className="text-sm text-traffic-red" role="status">
              {error}
            </p>
          ) : null}
        </form>
      )}
    </main>
  );
}
